/**
 * The design-system names cn() must understand (docs/THEME.md, STANDARD v3 sections 1.2-1.8 and 3.1).
 *
 * tailwind-merge only knows Tailwind's stock scales. A name it does not know is either read as the wrong group
 * (`text-body` as a COLOUR, so `cn('text-body', 'text-ink')` dropped the size) or never merged at all
 * (`cn('h-control', 'h-9')` kept both). Each list below extends one tailwind-merge theme scale or class group.
 * The values live in styles/design-tokens.css; design-system/v3-utilities.json is the spec both are tested
 * against (lib/utils.cn.test.ts, styles/designTokens.v3.compiled.test.ts).
 *
 * Erasable TypeScript only (plain consts), so node --experimental-strip-types can import it.
 */

/** Type roles (font-size utilities with line-height/weight companions), plus the legacy micro/dense pair. */
export const TYPE_ROLES = [
  'title', 'heading', 'label', 'body', 'field', 'meta', 'chip', 'section', 'read', 'read-dense', 'kpi', 'chart', 'code',
  'micro', 'dense',
] as const;

/** Two-axis dimensions in the `--spacing-*` namespace: h-, min-h-, w-, min-w- and size- all exist. */
export const SPACING_NAMES = ['control-sm', 'control', 'control-lg', 'avatar'] as const;

/** One-axis dimensions. */
export const HEIGHT_NAMES = ['chip'] as const;
export const MIN_HEIGHT_NAMES = ['header-bar', 'app-header', 'banner', 'row', 'row-compact', 'textarea', 'chip'] as const;
export const MIN_WIDTH_NAMES = ['chip'] as const;
export const WIDTH_NAMES = ['rail', 'popup-sm', 'popup', 'popup-lg'] as const;

/** Icon box utilities (width + height + stroke). */
export const ICON_CLASSES = ['icon-sm', 'icon', 'icon-lg'] as const;

/** Semantic radii, existing measured radii included (values per section 7). */
export const RADIUS_NAMES = ['control', 'card', 'float', 'tile', 'glow', 'hairline', 'tight', 'panel', 'overlay', 'item'] as const;

/** Max-width containers (`--container-*`, max-w-<name>): the dialog sizes (docs/THEME.md 2.9). */
export const CONTAINER_NAMES = ['dialog-sm', 'dialog-md', 'dialog-lg', 'dialog-xl'] as const;

/** Elevation and state glows (values per section 7). */
export const SHADOW_NAMES = [
  'micro', 'float', 'modal', 'raised', 'glow-brand', 'glow-success', 'glow-warning', 'glow-danger', 'glow-info', 'glow-soft', 'ai-glow',
  'tooltip', 'tooltip-strong',
] as const;

/** Owned z bands (section 1.7). */
export const Z_BANDS = [
  'sticky', 'header', 'banner', 'flyout', 'page-layer', 'modal-1-backdrop', 'modal-1', 'modal-1-over', 'modal-2-backdrop', 'modal-2', 'toast', 'skip',
] as const;

/** Motion (section 1.8). */
export const DURATION_NAMES = ['instant', 'fast', 'normal', 'slow'] as const;
export const EASE_NAMES = ['standard', 'emphasis', 'out-soft', 'live'] as const;
